/**
 * Coverage grid maths.
 *
 * Everything the app says about "can we afford to send this person to lunch?"
 * comes out of this module. It turns a day's shifts and lunch assignments into a
 * row of fixed-width buckets so the UI, the scorer and the validator all reason
 * about coverage from one shared, comparable structure instead of each
 * re-deriving it (and disagreeing at the edges).
 *
 * Two shapes of question are answered here, deliberately kept apart:
 *
 *  - `computeCoverage` builds the whole grid. It is called once per day per
 *    render/validation pass and is allowed to allocate.
 *  - `staffOnDutyAt` / `lunchesAt` answer one instant. The solver calls them
 *    thousands of times while scoring candidates, so they never build a grid.
 *
 * All times are minutes from midnight of the shift's own day, so an overnight
 * shift simply ends past 1440 and the arithmetic below needs no special case.
 */

import { MINUTES_PER_DAY, rangesOverlap } from '../core/time.js';
import { DEFAULT_SETTINGS, maxSimultaneousLunchesAt } from '../core/settings.js';

/** 06:00 - the earliest hour the timeline shows before it is widened to fit. */
const DEFAULT_TIMELINE_START = 360;

/**
 * Midnight closing the day. Only a shift finishing strictly after midnight
 * pushes the timeline past this, which is exactly what the night shifts should
 * do - the timeline renders the overflow rather than clipping it.
 */
const DEFAULT_TIMELINE_END = MINUTES_PER_DAY;

/** Breach codes carried on a bucket; validate.js maps these to warning codes. */
const BREACH_BELOW_MINIMUM = 'below-minimum';
const BREACH_TOO_MANY_SIMULTANEOUS = 'too-many-simultaneous';

/**
 * Shared result for "nobody is at lunch here". Frozen so an accidental push
 * cannot leak between calls - treat every `lunchesAt` result as read-only and
 * copy it before sorting.
 */
const NO_LUNCHES = Object.freeze([]);

/** Coerce a duration-ish setting, falling back when it is missing or nonsense. */
function positiveMinutes(value, fallback) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : fallback;
}

/** Bucket width, i.e. the granularity of the whole lunch grid. */
function bucketWidthFrom(settings) {
  return positiveMinutes(settings?.slotIntervalMinutes, DEFAULT_SETTINGS.slotIntervalMinutes);
}

/** How long one lunch blocks a person for. */
function lunchDurationFrom(settings) {
  return positiveMinutes(settings?.lunchDurationMinutes, DEFAULT_SETTINGS.lunchDurationMinutes);
}

/** Only records describing real worked time contribute to coverage. */
function isWorkedShift(shift) {
  return Boolean(shift)
    && shift.kind === 'shift'
    && Number.isFinite(shift.startMinutes)
    && Number.isFinite(shift.endMinutes)
    && shift.endMinutes > shift.startMinutes;
}

/** Finite slot start for an assignment, or null when it is not placed yet. */
function slotStartOf(assignment) {
  const slot = assignment?.slotMinutes;
  return Number.isFinite(slot) ? slot : null;
}

/** Defensive array access - callers hand us store data that may be undefined. */
function asArray(value) {
  return Array.isArray(value) ? value : [];
}

/**
 * Honour an explicit range when the caller supplies a usable one, otherwise
 * derive it. Callers that already know the window (the timeline view drawing a
 * whole week on one axis) must be able to force every day onto the same grid.
 */
function resolveRange(range, shifts, settings) {
  const start = Number(range?.start);
  const end = Number(range?.end);
  if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
    return { start, end };
  }
  return timelineRange(shifts, settings);
}

/**
 * The window the day should be drawn and bucketed over.
 *
 * A fixed 06:00 → midnight default keeps day cards visually comparable across
 * the week instead of each one zooming to its own contents. It then *widens*
 * (never narrows) to swallow anything outside: a 06:30 start pulls the left
 * edge back, and a 22:00–08:00 night pushes the right edge past 1440 so the
 * overnight tail is still rendered rather than silently cropped.
 *
 * The edges are snapped outwards onto the bucket grid so that
 * {@link computeCoverage} tiles the range with whole buckets and every bucket
 * boundary lands on a real slot time.
 */
export function timelineRange(shifts, settings) {
  const width = bucketWidthFrom(settings);
  const records = asArray(shifts);

  let start = DEFAULT_TIMELINE_START;
  let end = DEFAULT_TIMELINE_END;

  for (const shift of records) {
    if (!isWorkedShift(shift)) continue;
    if (shift.startMinutes < start) start = shift.startMinutes;
    if (shift.endMinutes > end) end = shift.endMinutes;
  }

  const snappedStart = Math.floor(start / width) * width;
  const snappedEnd = Math.ceil(end / width) * width;

  return {
    start: snappedStart,
    // Guarantees at least one bucket even for a degenerate configuration.
    end: Math.max(snappedEnd, snappedStart + width),
  };
}

/**
 * Build the coverage grid for one day.
 *
 * Buckets tile `[range.start, range.end)` at `settings.slotIntervalMinutes`, and
 * a shift or a lunch counts towards a bucket when it *overlaps* it rather than
 * when it merely starts in it. That distinction matters: a 45-minute lunch on a
 * 30-minute grid straddles two buckets, and the person is unavailable for both,
 * so anything that only tested the bucket start would happily double-book the
 * second half of their break.
 *
 * A bucket where nobody is rostered is reported as `ok` - an empty period is not
 * a coverage failure, it is simply closed, and flagging it would bury the real
 * breaches under noise from the quiet ends of the day. For the same reason a
 * period that is thinly staffed *before any lunch is placed* is reported with
 * `understaffed: true` rather than as a breach: see the comment beside the
 * calculation below.
 *
 * Pure: nothing passed in is read after the call and nothing is mutated.
 */
export function computeCoverage({ shifts, assignments, dayConfig, settings, range } = {}) {
  const shiftRecords = asArray(shifts);
  const lunchRecords = asArray(assignments);
  const width = bucketWidthFrom(settings);
  const lunchDuration = lunchDurationFrom(settings);
  const span = resolveRange(range, shiftRecords, settings);

  // Snapshot the limits onto every bucket so the UI can explain a breach
  // without needing the day config that produced it.
  const minRequired = Math.max(0, Math.round(Number(dayConfig?.minStaffOnDuty) || 0));
  const buckets = [];

  for (let bucketStart = span.start; bucketStart < span.end; bucketStart += width) {
    // The final bucket is clipped rather than allowed to run past the range, so
    // the grid never claims coverage for time the timeline does not show.
    const bucketEnd = Math.min(bucketStart + width, span.end);
    // NISCC permits only one weekday lunch before 13:00. Because the cap can
    // change during a day, snapshot the effective limit for this bucket rather
    // than copying one constant from the day profile.
    const maxSimultaneous = maxSimultaneousLunchesAt(dayConfig, bucketStart);

    const employeeIds = [];
    for (const shift of shiftRecords) {
      if (!isWorkedShift(shift)) continue;
      if (!rangesOverlap(shift.startMinutes, shift.endMinutes, bucketStart, bucketEnd)) continue;
      employeeIds.push(shift.employeeId);
    }

    const lunchEmployeeIds = [];
    for (const assignment of lunchRecords) {
      const slotMinutes = slotStartOf(assignment);
      if (slotMinutes === null) continue;
      if (!rangesOverlap(slotMinutes, slotMinutes + lunchDuration, bucketStart, bucketEnd)) continue;
      lunchEmployeeIds.push(assignment.employeeId);
    }

    const scheduled = employeeIds.length;
    const onLunch = lunchEmployeeIds.length;
    const remaining = scheduled - onLunch;

    // A period can be short of people for two very different reasons, and
    // conflating them makes the coverage bar useless:
    //
    //  * The rota simply has too few people rostered - 03:00 on a night shift,
    //    or the half hour before the early team arrives. Lunchly did not cause
    //    that and cannot fix it by moving a lunch. On the demo week alone this
    //    accounts for 18 of Monday's 52 buckets; reporting them as breaches
    //    paints the whole night red and buries the real problems.
    //  * A lunch we scheduled pushed cover under the floor. That IS our doing
    //    and is precisely what this module exists to catch.
    //
    // Only the second is a breach. The first is surfaced separately as
    // `understaffed` so the UI can still shade it as information.
    const understaffed = scheduled > 0 && scheduled < minRequired;
    const lunchCausedShortfall = onLunch > 0 && remaining < minRequired;

    const breaches = [];
    if (lunchCausedShortfall) breaches.push(BREACH_BELOW_MINIMUM);
    if (onLunch > maxSimultaneous) breaches.push(BREACH_TOO_MANY_SIMULTANEOUS);

    buckets.push({
      startMinutes: bucketStart,
      endMinutes: bucketEnd,
      scheduled,
      onLunch,
      remaining,
      minRequired,
      maxSimultaneous,
      ok: breaches.length === 0,
      /** Pre-existing thin cover, independent of any lunch. Informational. */
      understaffed,
      breaches,
      employeeIds,
      lunchEmployeeIds,
    });
  }

  return buckets;
}

/**
 * Find the bucket containing an instant.
 *
 * Lets the UI answer "what is the state at the time under this cursor / drop
 * target?" from an already-computed grid instead of recomputing coverage per
 * hover. Bucket ranges are half-open, so a time landing exactly on a boundary
 * belongs to the bucket that is starting, matching how a slot time reads.
 */
export function coverageAt(buckets, minutes) {
  if (!Array.isArray(buckets) || !Number.isFinite(minutes)) return null;
  for (const bucket of buckets) {
    if (minutes >= bucket.startMinutes && minutes < bucket.endMinutes) return bucket;
  }
  return null;
}

/**
 * How many people are on shift at one instant.
 *
 * The solver asks this for every candidate slot of every employee, so it stays
 * a plain indexed count: no grid, no intermediate arrays, no iterator objects.
 * Half-open `[start, end)` means somebody clocking off at 16:00 is not counted
 * as covering 16:00 - the same convention {@link computeCoverage} uses, which
 * is what keeps the scorer and the coverage strip agreeing with each other.
 */
export function staffOnDutyAt(shifts, minutes) {
  if (!Array.isArray(shifts) || !Number.isFinite(minutes)) return 0;
  let count = 0;
  for (let index = 0; index < shifts.length; index += 1) {
    const shift = shifts[index];
    if (!isWorkedShift(shift)) continue;
    if (shift.startMinutes <= minutes && minutes < shift.endMinutes) count += 1;
  }
  return count;
}

/**
 * Which assignments are mid-lunch at one instant.
 *
 * Returns the assignments themselves (never copies) because the caller needs
 * the names and locks to explain a clash, not just a count. A lunch occupies
 * `[slotMinutes, slotMinutes + lunchDurationMinutes)`, so a 30-minute break at
 * 12:30 still counts at 12:59 but not at 13:00.
 *
 * The result is read-only: the empty case is a shared frozen array so the
 * solver's hot loop allocates nothing on the common "slot is free" path.
 */
export function lunchesAt(assignments, minutes, lunchDurationMinutes) {
  if (!Array.isArray(assignments) || !Number.isFinite(minutes)) return NO_LUNCHES;
  const duration = positiveMinutes(lunchDurationMinutes, DEFAULT_SETTINGS.lunchDurationMinutes);

  let found = null;
  for (let index = 0; index < assignments.length; index += 1) {
    const assignment = assignments[index];
    const slotMinutes = slotStartOf(assignment);
    if (slotMinutes === null) continue;
    if (minutes < slotMinutes || minutes >= slotMinutes + duration) continue;
    if (found === null) found = [];
    found.push(assignment);
  }

  return found ?? NO_LUNCHES;
}
